import React from 'react'
import { FaDice as DiceIcon } from 'react-icons/fa'
import {
  useSequencerStore,
  selectCurrentProject,
  selectCurrentBuses,
  PADS_PER_PROJECT,
  type ScramblerSnapshot,
} from '@/stores/sequencerStore'
import type { ScramblerTarget } from '@/lib/scramblerRandomizer'

/** Module-level constant so the fallback returns the SAME reference
 *  every call. Previously the selector used `?? []` inline which
 *  minted a fresh `[]` on every store notification — that violates
 *  React 18's `useSyncExternalStore` requirement that snapshots be
 *  stable across calls with identical store state, and fires
 *  "Maximum update depth exceeded" (React error #185). */
const EMPTY_SCRAMBLES: readonly ScramblerSnapshot[] = []

/**
 * Compact Scrambler panel (formerly "Jungle Mode"). A collapsible
 * popover that toggles the performance-time transforms in
 * `@/lib/scramblerRandomizer`: velocity + pitch jitter,
 * reverse-every-N, step probability, downbeat stutter, and the
 * slice-scramble die-roll.
 *
 * The underlying pattern stays pristine — all transforms are
 * applied on the scheduler's fire path. Toggling the master
 * enable off returns the pattern to its literal playback. "Undo"
 * is the enable toggle; individual slider values persist through
 * an on/off cycle so the user can tweak a recipe once and flip
 * in and out of it during performance.
 */
const PANEL_HELP =
  'Real-time performance transforms — leave the pattern pristine, toggle Enabled off to revert. Values persist across toggles so you can flip in and out of a recipe live.'

export const ScramblerPanel: React.FC = () => {
  const params = useSequencerStore(s => s.scramblerParams)
  const setScramblerParams = useSequencerStore(s => s.setScramblerParams)
  const bumpScramblerSeed = useSequencerStore(s => s.bumpScramblerSeed)
  const saveScramblerSnapshot = useSequencerStore(s => s.saveScramblerSnapshot)
  const recallScramblerSnapshot = useSequencerStore(
    s => s.recallScramblerSnapshot,
  )
  const deleteScramblerSnapshot = useSequencerStore(
    s => s.deleteScramblerSnapshot,
  )
  const activeId = useSequencerStore(s => s.activeScramblerSnapshotId)
  const scrambles = useSequencerStore(
    s => selectCurrentProject(s)?.scramblerSnapshots ?? EMPTY_SCRAMBLES,
  )

  return (
    <details
      className="rounded-md bg-surface-sunken border border-brd-subtle"
      // Tooltip the whole panel so users who don't expand it still
      // learn what the Scrambler does. The per-knob help moves to each
      // Row's title attribute — no inline paragraphs, because they
      // pushed the FX rail's 6 bank buttons past the viewport on
      // iPad / Catalyst.
      title={PANEL_HELP}
    >
      <summary
        className={`flex items-center gap-2 px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] cursor-pointer select-none list-none ${
          params.enabled
            ? 'text-second'
            : 'text-content-tertiary hover:text-content-secondary'
        }`}
      >
        <span aria-hidden>▸</span>
        Scrambler
        {params.enabled && <span className="text-accent">●</span>}
        {params.scrambleSeed > 0 && (
          <span className="text-[9px] text-content-tertiary normal-case tracking-normal">
            · seed {params.scrambleSeed}
          </span>
        )}
      </summary>
      <div className="flex flex-col gap-2 px-2 pb-2 pt-1">
        <div className="flex items-center gap-2 flex-wrap">
          <label
            className="flex items-center gap-1.5 text-[10px] font-mono text-content-secondary"
            title="Master switch — when on, Scrambler transforms apply at fire time. When off, pattern plays literally."
          >
            <input
              type="checkbox"
              checked={params.enabled}
              onChange={e => setScramblerParams({ enabled: e.target.checked })}
              className="accent-[color:var(--accent)]"
            />
            Enabled
          </label>
          <button
            type="button"
            onClick={bumpScramblerSeed}
            title="Bump the scramble seed — remaps each sample pad's slice to a new permutation. Also enables the Scrambler if currently off."
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent transition-colors"
          >
            <DiceIcon size={9} />
            Scramble
          </button>
        </div>

        {/* Preset recall row — each saved snapshot gets a button
            labelled A/B/C/... (or its custom name). Active preset
            highlights; right-click (or long-press) to delete. The
            "+" button saves the current seed + params as a new
            preset. */}
        <div
          className="flex items-center gap-1 flex-wrap"
          title="Save + recall Scrambler snapshots. Each preset captures the current seed + all sliders. The + button saves; click a letter to recall."
        >
          <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-content-tertiary w-full">
            Presets
          </span>
          {scrambles.map(snap => {
            const isActive = activeId === snap.id
            const label = snap.name.startsWith('Scramble ')
              ? snap.name.slice(9)
              : snap.name
            return (
              <button
                key={snap.id}
                type="button"
                onClick={() => recallScramblerSnapshot(snap.id)}
                onContextMenu={e => {
                  e.preventDefault()
                  if (
                    window.confirm(
                      `Delete Scrambler preset "${snap.name}"? This can't be undone.`,
                    )
                  ) {
                    deleteScramblerSnapshot(snap.id)
                  }
                }}
                title={`Recall "${snap.name}" (right-click to delete)`}
                aria-pressed={isActive}
                className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-[0.12em] border transition-colors ${
                  isActive
                    ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                    : 'border-brd-subtle text-content-secondary hover:border-accent hover:text-accent'
                }`}
              >
                {label}
              </button>
            )
          })}
          <button
            type="button"
            onClick={() => {
              const name = window.prompt(
                'Name this scramble preset (or leave blank for A/B/C...)',
                '',
              )
              saveScramblerSnapshot(name?.trim() || undefined)
            }}
            title="Save current seed + params as a new preset"
            className="px-2 py-0.5 rounded text-[10px] font-mono border border-dashed border-brd-subtle text-content-tertiary hover:border-accent hover:text-accent transition-colors"
          >
            +
          </button>
        </div>

        <TargetSelector
          target={params.target}
          onChange={target => setScramblerParams({ target })}
        />

        <PrintToPatternRow />

        <Row
          label="Vel jitter"
          value={`±${params.velocityJitterPct}%`}
          title="Random ± velocity deviation per fired hit. 0 % plays exactly what you wrote; 10–30 % reads as humanised, 50 % is full chaos."
        >
          <input
            type="range"
            min={0}
            max={50}
            step={1}
            value={params.velocityJitterPct}
            onChange={e =>
              setScramblerParams({ velocityJitterPct: Number(e.target.value) })
            }
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        <Row
          label="Pitch jitter"
          value={`±${params.pitchJitterCents}¢`}
          title="Random ± detune per hit, in cents (100 ¢ = 1 semitone). Adds micro-tonal unease without shifting the pattern's tonal centre."
        >
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={params.pitchJitterCents}
            onChange={e =>
              setScramblerParams({ pitchJitterCents: Number(e.target.value) })
            }
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        <Row
          label="Reverse every"
          value={params.reverseEveryN === 0 ? 'off' : `${params.reverseEveryN}`}
          title="Flips sample playback on every Nth step. 0 disables. Try N = 4 on a hat line for a reverse-every-quarter effect."
        >
          <input
            type="range"
            min={0}
            max={16}
            step={1}
            value={params.reverseEveryN}
            onChange={e =>
              setScramblerParams({ reverseEveryN: Number(e.target.value) })
            }
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        <Row
          label="Step %"
          value={`${Math.round(params.stepProbability * 100)}%`}
          title="Probability each step actually fires. 100 % is normal playback; drop to 70–80 % for glitchy step dropouts that stay musical."
        >
          <input
            type="range"
            min={50}
            max={100}
            step={1}
            value={Math.round(params.stepProbability * 100)}
            onChange={e =>
              setScramblerParams({
                stepProbability: Number(e.target.value) / 100,
              })
            }
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        {/* Long-form pattern controls — A/A'/B structure across
            multiple bars. Default `1` keeps the pre-existing
            single-bar behaviour. */}
        <Row
          label="Form"
          value={
            params.scramblerBars === 1
              ? 'single bar'
              : params.scramblerBars === 2
                ? 'A A′ (2 bars)'
                : params.scramblerBars === 4
                  ? "A A' B A' (4 bars)"
                  : "A A A' B × 2 (8 bars)"
          }
          title="How many bars the Scrambler form repeats over. Longer forms build call-and-response structure so patterns don't feel loop-stuck. Set to 1 for per-bar independent scrambling (legacy behaviour)."
        >
          <select
            value={params.scramblerBars}
            onChange={e =>
              setScramblerParams({
                scramblerBars: Number(e.target.value) as 1 | 2 | 4 | 8,
              })
            }
            className="w-full text-[10px] font-mono rounded bg-surface-sunken border border-brd-subtle px-1 py-0.5 text-content-secondary focus:outline-none focus:border-accent"
          >
            <option value={1}>1 bar</option>
            <option value={2}>2 bars (A A′)</option>
            <option value={4}>4 bars (A A′ B A′)</option>
            <option value={8}>8 bars (A A A′ B × 2)</option>
          </select>
        </Row>

        {params.scramblerBars > 1 && (
          <Row
            label="Tightness"
            value={`${Math.round(params.formTightness * 100)}%`}
            title="How tightly repeated 'A' bars match each other. 100 % = every A is identical. 0 % = each A re-rolls jitter independently. 60 % is recognisable but breathing."
          >
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(params.formTightness * 100)}
              onChange={e =>
                setScramblerParams({
                  formTightness: Number(e.target.value) / 100,
                })
              }
              className="w-full accent-[color:var(--accent)]"
            />
          </Row>
        )}

        {/* Roll placement + probability. Replaces the binary
            stutter-downbeats checkbox (still honoured for back-compat
            but hidden — the new controls do everything it did
            plus more). */}
        <Row
          label="Roll placement"
          value={
            params.rollPlacement === 'downbeat'
              ? '1 (downbeat)'
              : params.rollPlacement === 'two-and-four'
                ? '2 + 4 (snare)'
                : 'mixed'
          }
          title="Where auto-rolls land. Downbeat = beat 1 only. 2+4 = snare-style rolls on beats 2 and 4 (default, DnB / jungle convention). Mixed = per-pad split so different pads roll at different slots."
        >
          <select
            value={params.rollPlacement}
            onChange={e =>
              setScramblerParams({
                rollPlacement: e.target.value as
                  | 'downbeat'
                  | 'two-and-four'
                  | 'mixed',
              })
            }
            className="w-full text-[10px] font-mono rounded bg-surface-sunken border border-brd-subtle px-1 py-0.5 text-content-secondary focus:outline-none focus:border-accent"
          >
            <option value="downbeat">Downbeat (beat 1)</option>
            <option value="two-and-four">2 + 4 (snare)</option>
            <option value="mixed">Mixed</option>
          </select>
        </Row>

        <Row
          label="Roll chance"
          value={`${Math.round(params.rollChancePerBar * 100)}%`}
          title="Per-bar probability that a roll actually fires at the chosen placement. 0 % = no auto-rolls (same as stutter-downbeats off). 100 % = every eligible slot rolls. 35 % default feels busy without getting frantic."
        >
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(params.rollChancePerBar * 100)}
            onChange={e =>
              setScramblerParams({
                rollChancePerBar: Number(e.target.value) / 100,
              })
            }
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        <label
          className="flex items-center gap-1.5 text-[10px] font-mono text-content-secondary"
          title="When a roll lands immediately before a silent step, push its tail into the master delay for a natural trail-off."
        >
          <input
            type="checkbox"
            checked={params.rollTailIntoSilence}
            onChange={e =>
              setScramblerParams({ rollTailIntoSilence: e.target.checked })
            }
            className="accent-[color:var(--accent)]"
          />
          Roll tail → delay
        </label>
      </div>
    </details>
  )
}

const Row: React.FC<{
  label: string
  value: string
  /** Single-sentence explanation that renders as a native
   *  tooltip on the whole row. Kept off-screen by default so the
   *  panel stays compact in narrow FX columns. */
  title?: string
  children: React.ReactNode
}> = ({ label, value, title, children }) => (
  <div title={title}>
    <div className="flex items-baseline justify-between mb-0.5">
      <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
        {label}
      </span>
      <span className="text-[9px] font-mono text-content-secondary tabular-nums">
        {value}
      </span>
    </div>
    {children}
  </div>
)

/**
 * Target selector — picks which pads the Scrambler transforms
 * apply to. Three options:
 *   - Bank (default): every pad in the active bank.
 *   - Pads: a specific pad subset chosen via the 1..16 chip row.
 *   - Bus: every pad currently routed through the named bus.
 *
 * Changing the kind resets the per-kind fields to safe defaults
 * (empty pad list, first-available bus) so we never land in a
 * "bus target but no busId" impossible state.
 */
const TargetSelector: React.FC<{
  target: ScramblerTarget
  onChange: (next: ScramblerTarget) => void
}> = ({ target, onChange }) => {
  const buses = useSequencerStore(selectCurrentBuses)
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
        Attached to
      </span>
      <div className="flex items-center gap-1 flex-wrap">
        <KindChip
          label="Bank"
          active={target.kind === 'bank'}
          title="Scrambler applies to every pad in the bank. The original Jungle Mode behaviour."
          onClick={() => onChange({ kind: 'bank' })}
        />
        <KindChip
          label="Pads"
          active={target.kind === 'pads'}
          title="Scrambler applies only to the pads you pick below. Other pads play exactly what you wrote."
          onClick={() =>
            onChange({
              kind: 'pads',
              padIndices: target.kind === 'pads' ? target.padIndices : [],
            })
          }
        />
        <KindChip
          label="Bus"
          active={target.kind === 'bus'}
          title="Scrambler applies only to pads routed through the selected bus."
          onClick={() =>
            onChange({
              kind: 'bus',
              busId:
                target.kind === 'bus' ? target.busId : (buses[0]?.id ?? ''),
            })
          }
        />
      </div>

      {target.kind === 'pads' && (
        <div
          className="grid grid-cols-8 gap-0.5"
          title="Click to toggle which pads the Scrambler affects."
        >
          {Array.from({ length: PADS_PER_PROJECT }, (_, i) => {
            const on = target.padIndices.includes(i)
            return (
              <button
                key={i}
                type="button"
                onClick={() =>
                  onChange({
                    kind: 'pads',
                    padIndices: on
                      ? target.padIndices.filter(p => p !== i)
                      : [...target.padIndices, i].sort((a, b) => a - b),
                  })
                }
                aria-pressed={on}
                className={`h-5 rounded text-[9px] font-mono border transition-colors ${
                  on
                    ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                    : 'border-brd-subtle text-content-tertiary hover:border-accent hover:text-accent'
                }`}
              >
                {i + 1}
              </button>
            )
          })}
        </div>
      )}

      {target.kind === 'bus' && (
        <select
          value={target.busId}
          onChange={e => onChange({ kind: 'bus', busId: e.target.value })}
          className="bg-surface-raised border border-brd-subtle rounded px-1 py-0.5 text-[10px] font-mono text-content-primary"
          title="Pads routed to this bus get scrambled; others play clean."
        >
          {buses.length === 0 ? (
            <option value="">(no buses — add one in the Inspector)</option>
          ) : (
            buses.map(b => (
              <option key={b.id} value={b.id}>
                {b.name ?? b.id}
              </option>
            ))
          )}
        </select>
      )}
    </div>
  )
}

/**
 * "Print to pattern" — bakes the live Scrambler output into a
 * new pattern in the same bank. When the scrambler's form length
 * (`scramblerBars`) is greater than 1, the printed pattern runs
 * `source.stepCount × scramblerBars` steps so each bar captures
 * its A / A' / B / A' form-slot variation.
 *
 * Success toasts through a transient inline label — no global
 * toast system needed for this one-off affordance.
 */
const PrintToPatternRow: React.FC = () => {
  const enabled = useSequencerStore(s => s.scramblerParams.enabled)
  const bars = useSequencerStore(s => s.scramblerParams.scramblerBars)
  const printScramblerToPattern = useSequencerStore(
    s => s.printScramblerToPattern,
  )
  const [status, setStatus] = React.useState<string | null>(null)
  const handlePrint = () => {
    const id = printScramblerToPattern()
    if (!id) {
      setStatus('Enable the Scrambler before printing.')
    } else {
      setStatus(
        bars === 1
          ? 'Printed — new pattern active in this bank.'
          : `Printed ${bars} variations into a new pattern.`,
      )
    }
    window.setTimeout(() => setStatus(null), 2800)
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={handlePrint}
        disabled={!enabled}
        title="Bake the current scramble into a new pattern in this bank. Multi-bar forms produce one variation per bar."
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] border transition-colors border-brd-subtle text-content-secondary hover:border-accent hover:text-accent disabled:opacity-50 disabled:hover:border-brd-subtle disabled:hover:text-content-secondary"
      >
        Print to pattern
      </button>
      {status && (
        <span className="text-[10px] text-content-tertiary font-mono">
          {status}
        </span>
      )}
    </div>
  )
}

const KindChip: React.FC<{
  label: string
  active: boolean
  title: string
  onClick: () => void
}> = ({ label, active, title, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    title={title}
    className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-[0.12em] border transition-colors ${
      active
        ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
        : 'border-brd-subtle text-content-secondary hover:border-accent hover:text-accent'
    }`}
  >
    {label}
  </button>
)
